import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";

interface QuickTip {
  id: string;
  icon: string;
  title: string;
  body: string;
  action?: { label: string; route: string };
  source: "heuristic" | "llm";
}

interface QuickTipsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuickTipsPanel: React.FC<QuickTipsPanelProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [tips, setTips] = useState<QuickTip[]>([]);
  const [loading, setLoading] = useState(false);
  const [llmEnabled, setLlmEnabled] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get("/auth/ai/quick-tips")
      .then((resp) => {
        if (cancelled) return;
        setTips(resp.data.tips || []);
        setLlmEnabled(!!resp.data.llmEnabled);
      })
      .catch(() => {
        if (cancelled) return;
        setTips([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.15)",
          zIndex: 998,
        }}
      />
      <aside
        style={{
          position: "fixed",
          top: 80,
          right: 24,
          width: "min(380px, calc(100vw - 48px))",
          maxHeight: "calc(100vh - 120px)",
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          color: "#1f2937",
          overflow: "hidden",
        }}
        role="dialog"
        aria-label="Quick Tips"
      >
        <header
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>⚡ Quick Tips</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              {llmEnabled ? "Personalised from your live data" : "Based on your tracked applications"}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close tips"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 20,
              color: "#6b7280",
              cursor: "pointer",
              padding: 4,
            }}
          >
            ×
          </button>
        </header>

        <div style={{ overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {loading && (
            <div style={{ padding: 20, textAlign: "center", color: "#6b7280", fontSize: 13 }}>
              Generating tips for you…
            </div>
          )}
          {!loading && tips.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#6b7280", fontSize: 13 }}>
              You're all set — no immediate suggestions. Keep applying!
            </div>
          )}
          {!loading &&
            tips.map((t) => (
              <div
                key={t.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderLeft: `3px solid ${t.source === "llm" ? "#2563eb" : "#6b7280"}`,
                  borderRadius: 8,
                  padding: 12,
                  background: "#ffffff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 18 }}>{t.icon}</span>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>{t.title}</div>
                  {t.source === "llm" && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        color: "#2563eb",
                        background: "#eff6ff",
                        padding: "2px 6px",
                        borderRadius: 10,
                        fontWeight: 500,
                      }}
                    >
                      AI
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, marginBottom: t.action ? 10 : 0 }}>
                  {t.body}
                </div>
                {t.action && (
                  <button
                    onClick={() => {
                      onClose();
                      navigate(t.action!.route);
                    }}
                    style={{
                      background: "#2563eb",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {t.action.label} →
                  </button>
                )}
              </div>
            ))}
        </div>
      </aside>
    </>
  );
};

export default QuickTipsPanel;
